import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import SidebarContext from "../../contexts/MyContext";
import { AudioHandler } from "../../Apihander/AudioRecording/AudioRecording";
import { errorToast, successToast } from "../../utils/notificationUtils";
import { PageHeader, Spinner, fmt, friendlyDate, recordingName, recordingSeconds } from "../Notes/notesShared";
import { POPULAR_LANGUAGES, TRANSLATE_LANGUAGES, TRANSLATE_MAX_TEXT_LENGTH, getLanguageLabel, translateText } from "../../utils/translate";
import { AiIc } from "./aiShared";
import "../Notes/notes.css";
import "./aiTools.css";

const SORTED_LANGUAGES = [...TRANSLATE_LANGUAGES].sort((a, b) => a.label.localeCompare(b.label));
const BUSY_PHASES = ["transcribing", "translating", "saving"];

// Names that start with the query rank above ones that only contain it.
const searchLanguages = (query) => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const starts = [];
    const contains = [];
    SORTED_LANGUAGES.forEach((item) => {
        const label = item.label.toLowerCase();
        if (label.startsWith(q) || item.code.toLowerCase() === q) starts.push(item);
        else if (label.includes(q)) contains.push(item);
    });
    return [...starts, ...contains];
};

// The A-Z list is split by first letter so a long list can be scanned.
const groupByLetter = (languages) => languages.reduce((groups, item) => {
    const letter = item.label[0].toUpperCase();
    (groups[letter] = groups[letter] || []).push(item);
    return groups;
}, {});

const LanguageRow = ({ item, selected, disabled, onPick }) => (
    <button
        type="button"
        className={`ai-lang compact ${selected ? "sel" : ""}`}
        disabled={disabled}
        onClick={() => onPick(item.code)}
    >
        <span className="ai-lang-name">{item.label}</span>
        <span className="ai-radio" />
    </button>
);

const StepRow = ({ state, label, detail }) => (
    <div className={`ai-step ${state}`}>
        <span className="ai-step-dot">
            {state === "done" && <AiIc name="check" size="s" />}
            {state === "active" && <Spinner />}
        </span>
        <span className="nt-grow">
            <span className="ai-step-label">{label}</span>
            {detail && <span className="ai-step-detail">{detail}</span>}
        </span>
    </div>
);

// Language on the left, its translation on the right; the recording is transcribed first when it has no transcript.
const TranslateLanguage = ({ audio, onBack, onTranscribe }) => {
    const { setFilterAudioData, setFavoriteAudio } = useContext(SidebarContext);

    const [language, setLanguage] = useState(null);
    const [query, setQuery] = useState("");
    const [phase, setPhase] = useState("idle");
    const [runLanguage, setRunLanguage] = useState(null);
    const [statusText, setStatusText] = useState("");
    const [errorText, setErrorText] = useState("");
    const [result, setResult] = useState(null);
    const [transcribedHere, setTranscribedHere] = useState(false);
    const [elapsed, setElapsed] = useState(0);

    const panelRef = useRef(null);
    const mountedRef = useRef(true);
    const startedRef = useRef(false);

    const transcript = audio?.transcribe?.isTranscribe ? audio.transcribe.transcribeText || "" : null;
    const transcribeFailed = audio?.transcribe?.status === "failed";
    const busy = BUSY_PHASES.includes(phase);
    const searching = Boolean(query.trim());

    const results = useMemo(() => searchLanguages(query), [query]);
    const groups = useMemo(() => groupByLetter(SORTED_LANGUAGES), []);

    useEffect(() => () => { mountedRef.current = false; }, []);

    // A running clock shows the wait is progressing, since transcription gives no percentage.
    useEffect(() => {
        if (!busy) return undefined;
        setElapsed(0);
        const startedAt = Date.now();
        const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
        return () => clearInterval(timer);
    }, [busy]);

    const fail = (message) => {
        setErrorText(message);
        setPhase("error");
    };

    const saveTranslation = async (code, text) => {
        const saved = await AudioHandler.doSaveTranslation(audio._id, { languageCode: code, translatedText: text });
        if (saved?.statusCode !== 200) return false;
        // Kept on the loaded lists so the recording's other screens show it without a refetch.
        const withTranslations = (item) => (item._id === audio._id ? { ...item, translations: saved.data.translations } : item);
        setFilterAudioData((prevData) => (prevData || []).map(withTranslations));
        setFavoriteAudio((prevFavorites) => (prevFavorites || []).map(withTranslations));
        return true;
    };

    const runTranslation = async (code, text) => {
        if (!text.trim()) return fail("No speech was found in this recording to translate.");
        if (text.length > TRANSLATE_MAX_TEXT_LENGTH) return fail(`The transcript is longer than ${TRANSLATE_MAX_TEXT_LENGTH} characters, so it can't be translated here.`);

        setPhase("translating");
        setStatusText("");
        try {
            const translated = await translateText({ text, targetLanguage: code, onStatus: (message) => mountedRef.current && setStatusText(message) });
            if (!mountedRef.current) return;
            setPhase("saving");
            const saved = await saveTranslation(code, translated.text);
            if (!mountedRef.current) return;
            setResult({ text: translated.text, language: code, saved, providerLabel: translated.providerLabel });
            setPhase("done");
        } catch (error) {
            if (mountedRef.current) fail(error?.message || "We couldn't translate this text right now. Please try again.");
        }
    };

    const handleTranslate = async () => {
        if (!language || busy) return;
        setRunLanguage(language);
        setResult(null);
        setErrorText("");
        // On a phone the panel sits below the list, so it is brought into view.
        panelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });

        if (transcript !== null) {
            setTranscribedHere(false);
            runTranslation(language, transcript);
            return;
        }

        setTranscribedHere(true);
        setPhase("transcribing");
        // Until the job is running, the recording still carries the status of any earlier attempt.
        startedRef.current = false;
        const started = await onTranscribe();
        startedRef.current = started;
        if (!started && mountedRef.current) fail("We couldn't start transcribing this recording. Please try again.");
    };

    // The context poll writes the finished transcript into the recording, which is the cue to translate.
    useEffect(() => {
        if (phase !== "transcribing") return;
        if (transcript !== null) {
            runTranslation(runLanguage, transcript);
        } else if (transcribeFailed && startedRef.current) {
            fail("Transcription failed, so there is no text to translate. Please try again.");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, transcript, transcribeFailed]);

    const handleSearchKeyDown = (e) => {
        // Enter takes the best match, so a language can be picked without the mouse.
        if (e.key === "Enter" && results[0]) setLanguage(results[0].code);
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(result.text);
            successToast("Translation copied");
        } catch (error) {
            errorToast("Couldn't copy the translation. Please select it and copy manually.");
        }
    };

    const handleDownload = () => {
        const blob = new Blob([result.text], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `${recordingName(audio).replace(/\.[^.]+$/, "") || "translation"}-${result.language}.txt`;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    const stepState = (step) => {
        const order = ["transcribing", "translating", "saving"];
        const current = order.indexOf(phase);
        const index = order.indexOf(step);
        if (current > index) return "done";
        if (current === index) return "active";
        return "pending";
    };

    const runLabel = getLanguageLabel(runLanguage);

    const renderPanel = () => {
        if (busy) {
            return (
                <div className="ai-tpanel-body">
                    <div className="ai-tpanel-status">
                        <h6>{phase === "transcribing" ? "Transcribing your recording" : `Translating to ${runLabel}`}</h6>
                    </div>
                    {transcribedHere
                        ? <StepRow state={stepState("transcribing")} label="Transcribe audio" detail={phase === "transcribing" ? "Turning speech into text" : "Transcript ready"} />
                        : <StepRow state="done" label="Transcript ready" detail="Already transcribed" />}
                    <StepRow state={stepState("translating")} label={`Translate to ${runLabel}`} detail={phase === "translating" ? statusText : ""} />
                    <StepRow state={stepState("saving")} label="Save to this recording" />
                </div>
            );
        }

        if (phase === "error") {
            return (
                <div className="ai-tpanel-empty">
                    <span className="ai-tpanel-icon error"><Icon icon="lucide:circle-alert" /></span>
                    <h6>Translation didn't finish</h6>
                    <p>{errorText}</p>
                    <button type="button" className="nt-btn outline" onClick={handleTranslate} disabled={!language}>Try again</button>
                </div>
            );
        }

        if (phase === "done" && result) {
            return (
                <div className="ai-tpanel-body">
                    <div className="ai-tpanel-meta">
                        <span className="nt-tag">{getLanguageLabel(result.language)}</span>
                        <span className={`nt-tag ${result.saved ? "ai-tag-ok" : "ai-tag-warn"}`}>{result.saved ? "Saved to recording" : "Not saved"}</span>
                        <span className="ai-tpanel-provider">via {result.providerLabel}</span>
                    </div>
                    <textarea className="ai-tpanel-text" readOnly value={result.text} aria-label={`Translation in ${getLanguageLabel(result.language)}`} />
                    <div className="ai-tpanel-actions">
                        <button type="button" className="nt-btn outline" onClick={handleCopy}><Icon icon="lucide:copy" /> Copy</button>
                        <button type="button" className="nt-btn outline" onClick={handleDownload}><AiIc name="down" size="s" /> Download .txt</button>
                    </div>
                </div>
            );
        }

        return (
            <div className="ai-tpanel-empty">
                <span className="ai-tpanel-icon"><Icon icon="lucide:languages" /></span>
                <h6>Your translation will appear here</h6>
                <p>
                    {language ? `Press "Translate to ${getLanguageLabel(language)}" to start.` : "Choose a language, then press Translate."}
                    {transcript === null && " This recording will be transcribed first."}
                </p>
            </div>
        );
    };

    return (
        <div className="nt vr-tone-sky">
            <PageHeader title="Translate" onBack={onBack} icon={<Icon icon="lucide:languages" />} tone="sky" />
            <div className="nt-content">
                <div className="ai-translate-split">
                    <div className="ai-translate-picker">
                        <div className="ai-item static">
                            <span className="nt-av rec"><AiIc name="mic" /></span>
                            <span className="nt-grow">
                                <span className="nt-title nt-block">{recordingName(audio)}</span>
                                <span className="nt-sub">{friendlyDate(audio?.createdAt)} • {fmt(recordingSeconds(audio))}</span>
                            </span>
                        </div>

                        <div className="nt-search ai-search">
                            <AiIc name="search" size="s" />
                            <input
                                autoFocus
                                placeholder={`Search ${TRANSLATE_LANGUAGES.length} languages`}
                                aria-label="Search languages"
                                value={query}
                                disabled={busy}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                            />
                            {searching && (
                                <button type="button" className="ai-search-clear" aria-label="Clear search" onClick={() => setQuery("")}>×</button>
                            )}
                        </div>

                        {searching ? (
                            <>
                                <div className="nt-caps ai-caps">
                                    {results.length ? `${results.length} ${results.length === 1 ? "match" : "matches"}` : "No matches"}
                                </div>
                                {results.length ? (
                                    <div className="ai-lang-grid">
                                        {results.map((item) => (
                                            <LanguageRow key={item.code} item={item} selected={language === item.code} disabled={busy} onPick={setLanguage} />
                                        ))}
                                    </div>
                                ) : (
                                    <p className="ai-lang-empty">No language matches "{query.trim()}". Try its English name, like "German" or "Japanese".</p>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="nt-caps ai-caps">Popular</div>
                                <div className="ai-lang-popular">
                                    {POPULAR_LANGUAGES.map((item) => (
                                        <button
                                            type="button"
                                            key={item.code}
                                            className={`ai-lang-chip ${language === item.code ? "sel" : ""}`}
                                            disabled={busy}
                                            onClick={() => setLanguage(item.code)}
                                        >
                                            <span className="ai-flag">{item.flag}</span>
                                            <span className="ai-lang-name">{getLanguageLabel(item.code)}</span>
                                        </button>
                                    ))}
                                </div>

                                <div className="nt-caps ai-caps ai-caps-all">All languages</div>
                                {Object.entries(groups).map(([letter, items]) => (
                                    <div key={letter}>
                                        <div className="ai-lang-letter">{letter}</div>
                                        <div className="ai-lang-grid">
                                            {items.map((item) => (
                                                <LanguageRow key={item.code} item={item} selected={language === item.code} disabled={busy} onPick={setLanguage} />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}

                        <div className="ai-lang-footer">
                            <button type="button" className="nt-btn block ai-cta" disabled={!language || busy} onClick={handleTranslate}>
                                {busy ? <><Spinner />{phase === "transcribing" ? "Transcribing…" : "Translating…"}</> : language ? `Translate to ${getLanguageLabel(language)}` : "Choose a language"}
                            </button>
                        </div>
                    </div>

                    <div className="ai-tpanel" ref={panelRef}>
                        <div className="ai-tpanel-head"><Icon icon="lucide:languages" className="ai-tpanel-head-icon" />Translated text</div>
                        {renderPanel()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TranslateLanguage;
